import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Switch,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  InputAdornment
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import {
  Person,
  Phone,
  Email,
  CreditCard,
  LocationOn,
  People
} from "@material-ui/icons";
import { enrollForEvent } from "../../api/event";
import { phoneNumber } from "../../utils/number";
import { branch } from "../../utils/Constant";

const useStyles = makeStyles(theme => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 20,
      maxWidth: 600,
      fontFamily: '"Poppins", sans-serif'
    }
  },
  dialogTitle: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: theme.spacing(3),
    textAlign: "center",
    fontFamily: '"Poppins", sans-serif'
  },
  dialogContent: {
    padding: theme.spacing(3, 3, 2, 3),
    fontFamily: '"Poppins", sans-serif',
    "& *": {
      fontFamily: '"Poppins", sans-serif !important'
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2)
    }
  },
  actions: {
    justifyContent: "space-between",
    padding: theme.spacing(2, 3, 3, 3),
    backgroundColor: "#fff",
    borderTop: "1px solid #e2e8f0"
  },
  tabsContainer: {
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(0)
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
    minHeight: 56,
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      gap: theme.spacing(1)
    }
  },
  textField: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#667eea"
        }
      },
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#667eea",
          borderWidth: 2
        }
      }
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#667eea"
    }
  },
  submitButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    padding: theme.spacing(1.5, 3.5),
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: 10,
    textTransform: "none",
    boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #5568d3 0%, #6540a0 100%)",
      boxShadow: "0 6px 16px rgba(102,126,234,0.5)",
      transform: "translateY(-2px)"
    },
    "&:disabled": {
      background: "#e0e0e0",
      color: "#9e9e9e"
    },
    transition: "all 0.3s ease"
  },
  cancelButton: {
    padding: theme.spacing(1.5, 3.5),
    fontSize: "1rem",
    borderRadius: 10,
    textTransform: "none",
    color: "#666",
    "&:hover": {
      backgroundColor: "#f5f5f5"
    }
  },
  priceInfo: {
    padding: theme.spacing(3),
    borderRadius: 16,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(236,72,153,0.3)"
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(0),
    color: "#2d3748"
  },
  helperText: {
    fontSize: "0.85rem",
    color: "#a0aec0",
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.4
  },
  customFieldContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: "#f7fafc",
    borderRadius: 16,
    border: "1px solid #e2e8f0"
  },
  requiredLabel: {
    "&::after": {
      content: '" *"',
      color: theme.palette.error.main
    }
  }
}));

// Validation schemas
const memberValidationSchema = Yup.object({
  nationalId: Yup.string()
    .matches(/^\d{12}$/, "No. Kad Pengenalan mesti 12 digit")
    .required("No. Kad Pengenalan diperlukan")
});

const associateValidationSchema = Yup.object({
  fullname: Yup.string()
    .min(3, "Nama terlalu pendek")
    .required("Nama penuh diperlukan"),
  nationalId: Yup.string()
    .matches(/^\d{12}$/, "No. Kad Pengenalan mesti 12 digit")
    .required("No. Kad Pengenalan diperlukan"),
  phone: Yup.string()
    .matches(/^01[0-9]{8,9}$/, "Nombor telefon tidak sah")
    .required("Nombor telefon diperlukan"),
  email: Yup.string()
    .email("E-mel tidak sah")
    .required("E-mel diperlukan"),
  branch: Yup.number()
    .min(1, "Sila pilih negeri")
    .required("Negeri diperlukan")
});

export default function EventDialog({
  id,
  fee,
  title,
  open,
  setOpen,
  setSucces,
  memberDiscount,
  customFields
}) {
  const [uri, setURI] = React.useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 for member, 1 for non-member
  const [price, setPrice] = useState(Number(fee));
  const [customValues, setCustomValues] = useState({});

  const classes = useStyles();
  const isMember = tabValue === 0;

  React.useEffect(() => {
    if (isMember && memberDiscount) {
      setPrice(Number(fee) - (Number(memberDiscount) / 100) * Number(fee));
    } else {
      setPrice(Number(fee));
    }
  }, [isMember, memberDiscount, fee]);

  React.useEffect(() => {
    if (uri) {
      return (window.location.href = uri);
    }
  }, [uri]);

  React.useEffect(() => {
    setTabValue(0);
    setCustomValues({});
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderCustomField = (key, customField) => {
    const { label, options, type } = customField;

    if (type === "Text") {
      return (
        <TextField
          label={label}
          type="text"
          variant="outlined"
          fullWidth
          className={classes.textField}
          onChange={e =>
            setCustomValues({
              ...customValues,
              [label]: e.target.value
            })
          }
        />
      );
    } else if (type === "Dropdown") {
      return (
        <Autocomplete
          options={options.split(",").map(opt => opt.trim())}
          getOptionLabel={option => option}
          onChange={(_e, value) => {
            setCustomValues({ ...customValues, [label]: value });
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              fullWidth
              className={classes.textField}
            />
          )}
        />
      );
    } else if (type === "Checkbox") {
      return (
        <FormGroup
          onChange={e =>
            setCustomValues({
              ...customValues,
              [label]: {
                ...customValues[label],
                [e.target.value]: e.target.checked
              }
            })
          }
        >
          {options.split(",").map(val => (
            <FormControlLabel
              key={val.trim()}
              value={val.trim()}
              control={<Checkbox color="primary" />}
              label={val.trim()}
            />
          ))}
        </FormGroup>
      );
    } else if (type === "Radio") {
      return (
        <FormControl component="fieldset">
          <RadioGroup
            aria-label={label}
            name={`radio-${key}`}
            onChange={e =>
              setCustomValues({ ...customValues, [label]: e.target.value })
            }
          >
            {options.split(",").map(val => (
              <FormControlLabel
                key={val.trim()}
                value={val.trim()}
                control={<Radio color="primary" />}
                label={val.trim()}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    } else if (type === "Switch") {
      return (
        <FormControlLabel
          control={
            <Switch
              color="primary"
              onChange={e =>
                setCustomValues({
                  ...customValues,
                  [key]: e.target.checked
                })
              }
            />
          }
          label={label}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="event-registration-dialog"
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle
        className={classes.dialogTitle}
        id="event-registration-dialog"
      >
        <Typography
          variant="h5"
          component="div"
          style={{
            fontWeight: 700,
            fontSize: "1.35rem",
            color: "#fff",
            lineHeight: 1.3
          }}
        >
          Pendaftaran {title}
        </Typography>
      </DialogTitle>

      <Formik
        initialValues={{
          nationalId: "",
          fullname: "",
          phone: "",
          email: "",
          branch: 0
        }}
        validationSchema={
          isMember ? memberValidationSchema : associateValidationSchema
        }
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          const { nationalId, fullname, phone, email, branch } = values;
          const obj = isMember
            ? { nationalId }
            : {
                fullname,
                nationalId,
                phone: phoneNumber(phone),
                email,
                branch
              };

          return enrollForEvent(isMember, id, obj, customValues)
            .then(res => {
              setSubmitting(false);
              if (res.url) {
                setURI(res.url);
              }
              setTimeout(() => setSucces(true), 2000);
              setOpen(false);
            })
            .catch(err => {
              setSubmitting(false);
              console.log(err);
              if (err.message === "Member already enrolled") {
                setErrors({
                  nationalId: "Anda sudah mendaftar untuk aktiviti ini"
                });
              }
              if (
                err.message ===
                "Cannot read property 'memberStatus' of undefined"
              ) {
                setErrors({
                  nationalId:
                    "Anda bukan ahli. Sila hubungi pasukan kami untuk bantuan lanjut"
                });
              }
              if (err.message === "Membership is not active") {
                setErrors({
                  nationalId: "Keahlian anda tidak aktif. Sila perbaharui"
                });
                setTimeout(() => (window.location.href = "/renew"), 1500);
              }
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue
        }) => (
          <>
            <DialogContent className={classes.dialogContent}>
              {/* Member Type Tabs */}
              <Box className={classes.tabsContainer}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab
                    label="Ahli GEMA"
                    icon={<Person />}
                    className={classes.tab}
                  />
                  <Tab
                    label="Peserta Biasa"
                    icon={<People />}
                    className={classes.tab}
                  />
                </Tabs>
              </Box>

              {/* Helper Text */}
              <Typography variant="body2" className={classes.helperText}>
                {isMember
                  ? "Daftar menggunakan No. Kad Pengenalan yang berdaftar"
                  : "Isi semua maklumat yang diperlukan untuk pendaftaran"}
              </Typography>

              {/* Member Form */}
              {isMember ? (
                <Box>
                  <TextField
                    name="nationalId"
                    label="No. Kad Pengenalan"
                    placeholder="Contoh: 990101011234"
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nationalId}
                    error={touched.nationalId && Boolean(errors.nationalId)}
                    helperText={touched.nationalId && errors.nationalId}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CreditCard />
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ maxLength: 12 }}
                  />
                </Box>
              ) : (
                // Non-Member Form
                <Box>
                  <TextField
                    name="fullname"
                    label="Nama Penuh"
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.fullname}
                    error={touched.fullname && Boolean(errors.fullname)}
                    helperText={touched.fullname && errors.fullname}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    name="nationalId"
                    label="No. Kad Pengenalan"
                    placeholder="Contoh: 990101011234"
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nationalId}
                    error={touched.nationalId && Boolean(errors.nationalId)}
                    helperText={touched.nationalId && errors.nationalId}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CreditCard />
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ maxLength: 12 }}
                  />

                  <Autocomplete
                    options={branch}
                    getOptionLabel={option => option.title}
                    onChange={(_e, val) =>
                      setFieldValue("branch", val ? val.value : 0)
                    }
                    onBlur={handleBlur}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Negeri"
                        variant="outlined"
                        fullWidth
                        required
                        className={classes.textField}
                        error={touched.branch && Boolean(errors.branch)}
                        helperText={touched.branch && errors.branch}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationOn />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />

                  <TextField
                    name="phone"
                    label="No. Telefon Bimbit"
                    placeholder="Contoh: 0123456789"
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phone}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    name="email"
                    label="E-mel"
                    type="email"
                    placeholder="Contoh: nama@email.com"
                    variant="outlined"
                    fullWidth
                    required
                    className={classes.textField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
              )}

              {/* Price Info */}
              {fee > 0 && (
                <Box className={classes.priceInfo}>
                  <Typography
                    variant="body2"
                    style={{
                      opacity: 0.95,
                      fontWeight: 500,
                      marginBottom: 4
                    }}
                  >
                    Yuran Pendaftaran
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      fontWeight: 800,
                      letterSpacing: "-0.5px"
                    }}
                  >
                    RM{price.toFixed(2)}
                  </Typography>
                  {isMember && memberDiscount > 0 && (
                    <Typography
                      variant="caption"
                      style={{
                        color: "#fff",
                        opacity: 0.9,
                        fontWeight: 500
                      }}
                    >
                      Jimat {memberDiscount}% sebagai ahli!
                    </Typography>
                  )}
                </Box>
              )}

              {/* Custom Fields */}
              {customFields && Object.keys(customFields).length > 0 && (
                <Box className={classes.customFieldContainer}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Maklumat Tambahan
                  </Typography>
                  {Object.keys(customFields).map(key => {
                    if (customFields[key].disabled) {
                      return null;
                    }
                    return (
                      <Box key={key} mb={2}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          style={{ fontWeight: 600, marginBottom: 8 }}
                        >
                          {customFields[key].label}
                        </Typography>
                        {renderCustomField(key, customFields[key])}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DialogContent>

            <DialogActions className={classes.actions}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                className={classes.cancelButton}
              >
                Batal
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={classes.submitButton}
                variant="contained"
                startIcon={
                  isSubmitting && <CircularProgress size={20} color="inherit" />
                }
              >
                {isSubmitting ? "Memproses..." : "Daftar Sekarang"}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
}
